import {
  BoundCheckboxField,
  BoundMultiSelectField,
  BoundTextAreaField,
  BoundTextField,
  Button,
  Css,
  IconButton,
  ModalProps,
  useComputed,
} from "@homebound/beam";
import { useState } from "react";
import { SaveAttachmentModel } from "src/components/boundAttachments/BoundAttachments";
import { BoundSingleAttachment } from "src/components/boundAttachments/BoundSingleAttachment";
import { HasIdAndName } from "src/utils";
import { ObjectState } from "src/utils/formState";
import { AddConstraintItemModal, ConstraintModalConfig } from "../../task-catalog/components/AddConstraintItemModal";
import { SaveGlobalPlanMilestoneFormValue } from "../MilestoneCatalogForm";

type AddAdditionalMilestoneCardProps = {
  formState: ObjectState<SaveGlobalPlanMilestoneFormValue>;
  globalPlanConstraintItems: HasIdAndName[];
  openModal: (modal: ModalProps) => void;
};

type AdditionalMilestoneCardItem = {
  name: string | undefined;
  constraints: string[];
  isDefault: boolean;
  isHomeownerVisible: boolean;
  homeownerName: string;
  homeownerDescription: string;
  homeownerIcon?: SaveAttachmentModel;
};

export function AddAdditionalMilestoneCard(props: AddAdditionalMilestoneCardProps) {
  const { formState, globalPlanConstraintItems, openModal } = props;
  const additionalMilestoneCardItems = formState.additionalMilestoneCards;
  const [newAdditionalMilestoneCardItem, setAdditionalMilestoneCardItem] = useState<AdditionalMilestoneCardItem>({
    constraints: [],
    name: undefined,
    isDefault: false,
    isHomeownerVisible: false,
    homeownerName: "",
    homeownerDescription: "",
    homeownerIcon: undefined,
  });

  const additionalMilestoneCards = useComputed(() => {
    return additionalMilestoneCardItems.rows.map((milestone, i) => {
      const { id } = milestone.value;
      const isHomeownerVisible = milestone.isHomeownerVisible.value === true;
      return (
        <div css={Css.bgWhite.br8.p3.mb3.$} key={"mci" + (id || i)} data-testid="additionalMilestoneCard">
          <div css={Css.tar.$}>
            <IconButton
              data-testid="removeAdditionalMilestoneCard"
              icon="x"
              onClick={() => additionalMilestoneCardItems.remove(i)}
            />
          </div>
          <div css={Css.df.fdc.gap2.$}>
            <BoundTextField label="Milestone Name" field={milestone.name} fullWidth />
            <BoundMultiSelectField
              field={milestone.constraints}
              label="Constraints"
              options={globalPlanConstraintItems ?? []}
            />
            <Button
              label="Add New"
              variant="text"
              onClick={() =>
                openModal({
                  content: (
                    <AddConstraintItemModal
                      constraintType={ConstraintModalConfig.ConstraintItem}
                      fieldState={milestone.constraints}
                    />
                  ),
                })
              }
            />
            <div css={Css.mt2.$}>
              <BoundCheckboxField
                label="Set as default milestone"
                field={milestone.isDefault}
                onChange={(val) => {
                  milestone.isDefault.set(val);
                  formState.isAllDefaultSelected.set(null);
                }}
              />
            </div>
            <BoundCheckboxField label="Show in the Homeowner Portal" field={milestone.isHomeownerVisible} />
            {/* Homeowner Portal Section */}
            <div css={Css.df.fdc.gap2.mt2.$}>
              <BoundTextField
                label="Homeowner Milestone Name"
                field={milestone.homeownerName}
                placeholder={milestone.name.value || "Enter milestone name"}
                disabled={!isHomeownerVisible}
              />
              <BoundTextAreaField
                label="Homeowner Description"
                field={milestone.homeownerDescription}
                placeholder="Enter a short description"
                required={isHomeownerVisible}
                disabled={!isHomeownerVisible}
              />
              <BoundSingleAttachment
                field={milestone.homeownerIcon}
                title={isHomeownerVisible ? "Icon *" : "Icon"}
                uppyUploaderProps={{ dragDropHeight: 96, dragDropWidth: 96 }}
              />
            </div>
          </div>
        </div>
      );
    });
  }, [additionalMilestoneCardItems, globalPlanConstraintItems]);

  return (
    <div>
      {additionalMilestoneCards}
      <div css={Css.br4.bsDashed.bgBlue50.blue500.bw("2px").py1.df.jcc.ma.maxwPx(600).mt4.$}>
        <Button
          label="Add another milestone"
          onClick={() => {
            additionalMilestoneCardItems.add({
              constraints: newAdditionalMilestoneCardItem.constraints,
              name: newAdditionalMilestoneCardItem.name,
              isDefault: newAdditionalMilestoneCardItem.isDefault,
              groups: formState.groups.value,
              isHomeownerVisible: newAdditionalMilestoneCardItem.isHomeownerVisible,
              homeownerName: newAdditionalMilestoneCardItem.homeownerName,
              homeownerDescription: newAdditionalMilestoneCardItem.homeownerDescription,
              homeownerIcon: newAdditionalMilestoneCardItem.homeownerIcon,
            });
            setAdditionalMilestoneCardItem((prevState) => ({
              ...prevState,
              name: undefined,
              isDefault: false,
              constraints: [],
              isHomeownerVisible: false,
              homeownerName: "",
              homeownerDescription: "",
              homeownerIcon: undefined,
            }));
          }}
          variant="text"
          icon="plus"
        />
      </div>
    </div>
  );
}
